import { useEffect, useState } from "react";

/**
 * Reactive wrapper around `window.matchMedia` for tier-based UI decisions.
 * Returns `false` during SSR. Subscribes to changes via the native
 * MediaQueryList listener so the component re-renders when the viewport
 * crosses the breakpoint.
 *
 * @example
 *   const isMobile = useMediaQuery("(max-width: 767px)");
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [query]);

  return matches;
}
